import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { AddOrderItemModalProps } from "../interfaces/modals";
import {
  useListProductsPicker,
  useListProductItemsByProduct,
  type ProductItem,
  type ProductPickerRow
} from "../../../hooks/api/useOrderInventory";
import {
  useAddOrderItem,
  useConfirmOrderItem
} from "../../../hooks/api/useSalesOrder";
import { isOrderMutationRejectedError } from "../../../hooks/api/orderMutationErrors";
import { SellingMode } from "../enums/orderStatus";
import { normalizeSellingMode } from "../utils/scanResponseUtils";
import { messageForOrderMutationCode } from "../utils/orderErrorMessages";

function resolveSellingMode(product: ProductPickerRow | null): string {
  if (!product?.sellingMode) return SellingMode.SINGLE_ITEM;
  return normalizeSellingMode(product.sellingMode) || SellingMode.SINGLE_ITEM;
}

const AddOrderItemModal: React.FC<AddOrderItemModalProps> = ({
  open,
  onClose,
  orderId,
  onAdded
}) => {
  const { t } = useKivunovaTranslation();

  const { mutateAsync: loadProducts, isPending: productsLoading } =
    useListProductsPicker();
  const { mutateAsync: loadItems, isPending: itemsLoading } =
    useListProductItemsByProduct();
  const addOrderItem = useAddOrderItem({ suppressErrorBanner: true });
  const confirmOrderItem = useConfirmOrderItem({ suppressErrorBanner: true });

  const loadProductsRef = useRef(loadProducts);
  const loadItemsRef = useRef(loadItems);
  useEffect(() => {
    loadProductsRef.current = loadProducts;
  }, [loadProducts]);
  useEffect(() => {
    loadItemsRef.current = loadItems;
  }, [loadItems]);

  const [products, setProducts] = useState<ProductPickerRow[]>([]);
  const [product, setProduct] = useState<ProductPickerRow | null>(null);
  const [items, setItems] = useState<ProductItem[]>([]);
  const [item, setItem] = useState<ProductItem | null>(null);
  const [quantityStr, setQuantityStr] = useState("1");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productsInfo, setProductsInfo] = useState<string | null>(null);
  const [itemsError, setItemsError] = useState<string | null>(null);

  const sellingMode = useMemo(() => {
    if (!product) return null;
    return resolveSellingMode(product);
  }, [product]);

  const resetLocalState = useCallback(() => {
    setProducts([]);
    setProduct(null);
    setItems([]);
    setItem(null);
    setQuantityStr("1");
    setSubmitError(null);
    setProductsError(null);
    setProductsInfo(null);
    setItemsError(null);
  }, []);

  // Load products when modal opens.

  // Load products when modal opens.
  useEffect(() => {
    if (!open) {
      resetLocalState();
      return;
    }

    let cancelled = false;
    setProductsError(null);
    setProductsInfo(null);

    (async () => {
      try {
        const list = await loadProductsRef.current({ page: 0, size: 100 });
        if (cancelled) return;

        setProducts(list);

        if (list.length === 0) {
          setProductsInfo(
            t(
              "add-item-no-products-catalog",
              "No products are available yet. Add products in inventory, then open this dialog again."
            )
          );
        }
      } catch {
        if (cancelled) return;

        setProducts([]);
        setProductsError(
          t(
            "add-item-products-load-failed",
            "Could not load products. Check your connection and try again."
          )
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, resetLocalState]);

  // Load items when a product is selected.
  useEffect(() => {
    if (!open || !product) {
      setItems([]);
      setItem(null);
      setItemsError(null);
      return;
    }
    let cancelled = false;
    setItemsError(null);

    (async () => {
      try {
        const raw = await loadItemsRef.current({ productId: product.id });
        if (cancelled) return;
        const eligible = raw.filter(
          (i) => i.state === "IN_STOCK" && i.remainingQuantity > 0
        );
        setItems(eligible);
      } catch {
        if (!cancelled) {
          setItems([]);
          setItemsError(
            t(
              "add-item-items-load-failed",
              "Could not load product items. Check your connection and try again."
            )
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, product]);

  // Reset quantity whenever the selected item or selling mode changes.
  useEffect(() => {
    if (!item || !sellingMode) return;
    setQuantityStr("1");
  }, [item, sellingMode]);

  const maxQty = item?.remainingQuantity ?? 0;
  const maxUnitInt = Math.max(1, Math.floor(maxQty));

  const validateAndSubmit = async () => {
    setSubmitError(null);

    if (!item || !product) {
      setSubmitError(
        t("add-item-select-product-item", "Select a product and product item.")
      );
      return;
    }

    if (!sellingMode) {
      setSubmitError(
        t("add-item-select-product-item", "Select a product and product item.")
      );
      return;
    }

    if (sellingMode === SellingMode.MEASURED) {
      const q = parseFloat(quantityStr.replace(",", "."));
      if (Number.isNaN(q) || q <= 0) {
        setSubmitError(
          t("add-item-invalid-qty", "Enter a quantity greater than zero.")
        );
        return;
      }
      if (q > maxQty) {
        setSubmitError(
          t("add-item-qty-exceeds", "Quantity cannot exceed available amount.")
        );
        return;
      }
      try {
        const scan = await confirmOrderItem.mutateAsync({
          orderId,
          productItemId: item.id,
          quantity: q
        });
        if (scan) {
          await onAdded();
          onClose();
        } else {
          setSubmitError(
            t(
              "add-item-failed-no-detail",
              "Could not add this item. Please try again."
            )
          );
        }
      } catch (e) {
        if (isOrderMutationRejectedError(e)) {
          setSubmitError(messageForOrderMutationCode(e.apiCode, t));
        } else {
          setSubmitError(
            t(
              "add-item-unexpected-error",
              "Something went wrong. Please try again."
            )
          );
        }
      }
      return;
    }

    const qty =
      sellingMode === SellingMode.SINGLE_ITEM ? 1 : parseInt(quantityStr, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      setSubmitError(
        t("add-item-invalid-qty", "Enter a valid whole number quantity.")
      );
      return;
    }
    if (qty > maxUnitInt) {
      setSubmitError(
        t("add-item-qty-exceeds", "Quantity cannot exceed available amount.")
      );
      return;
    }

    try {
      const order = await addOrderItem.mutateAsync({
        orderId,
        itemId: item.id,
        quantity: qty
      });
      if (order) {
        await onAdded();
        onClose();
      } else {
        setSubmitError(
          t(
            "add-item-failed-no-detail",
            "Could not add this item. Please try again."
          )
        );
      }
    } catch (e) {
      if (isOrderMutationRejectedError(e)) {
        setSubmitError(messageForOrderMutationCode(e.apiCode, t));
      } else {
        setSubmitError(
          t(
            "add-item-unexpected-error",
            "Something went wrong. Please try again."
          )
        );
      }
    }
  };

  const submitBusy = addOrderItem.isPending || confirmOrderItem.isPending;

  const quantityField =
    !product || !sellingMode ? (
      <TextField
        label={t("quantity", "Quantity")}
        value=""
        fullWidth
        size="small"
        disabled
        placeholder={t(
          "add-item-pick-product-for-qty",
          "Select a product first"
        )}
      />
    ) : sellingMode === SellingMode.SINGLE_ITEM ? (
      <TextField
        label={t("quantity", "Quantity")}
        value="1"
        fullWidth
        disabled
        size="small"
        helperText={t(
          "add-item-single-item-hint",
          "This product is sold as a single item (quantity is always 1)."
        )}
      />
    ) : sellingMode === SellingMode.UNIT_BASED ? (
      <TextField
        label={t("quantity", "Quantity")}
        type="number"
        value={quantityStr}
        onChange={(e) => setQuantityStr(e.target.value)}
        fullWidth
        size="small"
        disabled={submitBusy || !item}
        inputProps={{ min: 1, max: maxUnitInt, step: 1 }}
        helperText={`${t("add-item-unit-hint", "Whole units only.")} ${t("max", "Max")}: ${maxUnitInt}.`}
      />
    ) : (
      <TextField
        label={t("quantity", "Quantity")}
        type="number"
        value={quantityStr}
        onChange={(e) => setQuantityStr(e.target.value)}
        fullWidth
        size="small"
        disabled={submitBusy || !item}
        inputProps={{ min: 0, max: maxQty, step: 0.01 }}
        helperText={`${t(
          "add-item-measured-hint",
          "Measured sale (e.g. weight or length). Fractional quantities may be rounded when saving."
        )} ${t("max", "Max")}: ${maxQty}.`}
      />
    );

  return (
    <Modal open={open} onClose={submitBusy ? undefined : onClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("add-items-to-order", "Add items to order")}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={submitBusy}>
            <CloseIcon />
          </IconButton>
        </Box>

        {(productsError || productsInfo) && (
          <Alert
            severity={productsError ? "error" : "info"}
            sx={{ mb: 1 }}
            onClose={() => {
              setProductsError(null);
              setProductsInfo(null);
            }}
          >
            {productsError ?? productsInfo}
          </Alert>
        )}

        {itemsError && (
          <Alert
            severity="error"
            sx={{ mb: 1 }}
            onClose={() => setItemsError(null)}
          >
            {itemsError}
          </Alert>
        )}

        <Autocomplete
          options={products}
          getOptionLabel={(o) => `${o.name} (${o.id})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={product}
          onChange={(_, v) => setProduct(v)}
          loading={productsLoading}
          disabled={submitBusy}
          slotProps={{
            popper: {
              disablePortal: true,
              placement: "bottom-start",
              sx: { zIndex: (theme) => theme.zIndex.modal + 2 }
            }
          }}
          noOptionsText={
            productsLoading
              ? t("loading", "Loading…")
              : productsError
                ? t(
                    "add-item-product-dropdown-error",
                    "Products could not be loaded."
                  )
                : products.length === 0
                  ? t(
                      "add-item-no-products-to-pick",
                      "No products to choose from."
                    )
                  : t("no-matches", "No matches")
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("product", "Product")}
              size="small"
              margin="normal"
            />
          )}
        />

        <Autocomplete
          options={items}
          getOptionLabel={(o) => o.variationName?.trim() || o.id}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={item}
          onChange={(_, v) => setItem(v)}
          loading={itemsLoading}
          disabled={submitBusy || !product}
          slotProps={{
            popper: {
              disablePortal: true,
              placement: "bottom-start",
              sx: { zIndex: (theme) => theme.zIndex.modal + 2 }
            }
          }}
          noOptionsText={t(
            "add-item-no-stock",
            "No in-stock items with quantity for this product."
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("product-item", "Product item")}
              size="small"
              margin="normal"
            />
          )}
        />

        {product && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            {t("selling-mode", "Selling mode")}: {sellingMode}
            {product.baseUnit
              ? ` · ${t("base-unit", "Base unit")}: ${product.baseUnit}`
              : ""}
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>{quantityField}</Box>

        {submitError && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
        >
          <LoadingButton
            variant="outlined"
            onClick={onClose}
            disabled={submitBusy}
            sx={{ textTransform: "none" }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            loading={submitBusy}
            onClick={() => void validateAndSubmit()}
            disabled={!item || productsLoading || itemsLoading}
            sx={{
              textTransform: "none",
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("add-to-order", "Add to order")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddOrderItemModal;
